import { useMemo, useRef } from 'react';
import { useQueries, keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { zabierzWczesnaListe } from '@/lib/wczesnaLista';
import { WARSAW_DISTRICT_NORMALIZE } from '@/data/warsawDistrictAliases';
import {
  applyPinSort,
  PINS_LIMIT,
  PIN_COLUMNS,
  SERP_COLUMNS,
  SERP_PAGE_SIZE,
  applySerpFilters,
  getNextSerpPageParam,
  serpFilterKey,
  sciezkaOdczytu,
  applySekcja,
  type SekcjaListy,
} from './serpQuery';
import { maMiekkieFiltry } from '@/lib/filtryMiekkie';
// Ścieżka odczytu (wybór tabela/obszar/ranking, zapytanie o listę) mieszka od F3 w
// `src/lib/serpRead/` — module wspólnym z funkcją `send-filter-alerts` (plan §1.68). Klienta
// podaje ten plik; moduł go nie tworzy.
import { SERP_TABLE, buildQuery, serpSelect as serpSelectModulu, type SerpClient } from '@/lib/serpRead';

/** Typy zestawu filtrów mieszkają w module ścieżki odczytu (F3); stąd eksport dla dotychczasowych importów. */
export type { SortKey, SourcePostFilters } from '@/lib/serpRead/types';
import type { SourcePostFilters } from '@/lib/serpRead/types';


export type PostKind =
  | 'offer_whole_apartment'
  | 'offer_room'
  | 'tenant_seeking'
  | 'roommate_seeking'
  | 'not_a_listing'
  | 'unclear';

export type PostingType = 'individual' | 'agency' | 'unclear' | null;

/**
 * One offer as the FRONT sees it — a row of the serving layer, `flat.offers`.
 *
 * The name is now a historical artefact and is kept deliberately. Until Phase 3
 * this type described a row of `flat.source_posts`, because the raw post WAS the
 * offer: the SERP read cluster heads off the raw table and the offer page
 * re-derived a cluster client-side from a 50-row guess. It is not that any more
 * (Convention 20 / docs/architecture/offers-etl-model.md) — `flat.offers` is a
 * distinct entity with its own surrogate key, and a claimed offer is a
 * standalone owner row, not a scraped row with an overlay. Renaming the type,
 * and with it the ~284 `extracted_*` field references it carries, is a separate
 * mechanical PR; doing it inside the cutover would bury the behavioural diff in
 * a rename.
 *
 * What that leaves here is a type whose FIELD names still speak the raw layer's
 * dialect (`extracted_rent_pln`, `raw_text`) while its VALUES come from the
 * serving layer — the `alias:column` seam in SERP_COLUMNS and useSourcePost.
 * The seam is explicit in one place each and disappears with that rename.
 *
 * The raw layer's own columns live on `RawSourcePost` below. They are not
 * absent from this type by oversight: `flat.offers` genuinely does not have
 * them, and the admin console — the one legitimate reader of the raw layer — is
 * where they belong.
 */
export interface SourcePost {
  /**
   * `flat.offers.id` — a surrogate uuid ([D2]), NOT a `source_posts.id`.
   *
   * This is the single most consequential change of the migration: every
   * `/offer/:id` URL, every `offer_id` a demand row carries, every claim bridge
   * now keys on this. It is durable across cluster merges (a merge is an UPDATE
   * of `flat.offer_post_map`, not a new key) and it resolves to nothing in the
   * raw layer, which is exactly the point.
   */
  id: string;
  /**
   * Supply origin on the serving layer (`flat.offers.origin`):
   *   - 'scraped'    — projected from a source_posts cluster, no owner behind it
   *   - 'claimed'    — an owner took over a scraped offer; the row is now a
   *                    standalone owner entity (no re-scrape, no enrichment)
   *   - 'owner_only' — a manual owner listing, never scraped
   * The two owner-driven values behave identically on the front, so test
   * `origin !== 'scraped'` rather than enumerating them — a future fourth
   * owner-side origin is then covered the day it is added.
   */
  origin?: 'scraped' | 'claimed' | 'owner_only';
  /**
   * The owner's `flat.listings` row behind this offer — set on the owner arm
   * (claimed + owner_only), null on scraped. The messaging path keys on it.
   * This replaces the old `useListingOverlay` round trip: the projection has
   * already merged the listing into the offer, so the front never joins the two
   * at read time.
   */
  listing_id?: string | null;
  source: string;
  source_external_id: string;
  source_url: string;
  /**
   * The group/board the offer came from. NOT granted to anon on `flat.offers` —
   * useSourcePost hydrates it from the offer's HEAD row in
   * `flat.offer_post_map`, where the same value IS granted. Absent on the owner
   * arm, which has no channel.
   */
  channel_url: string | null;
  channel_name: string | null;
  /** `flat.offers.description` — the scraped body, or the owner's own text. */
  raw_text: string | null;
  /** `flat.offers.total_floors` — liczba pięter budynku (LIST-09); tylko strona oferty. */
  total_floors?: number | null;
  /**
   * Durable-only by construction ([P11]): the projection writes
   * `photos_storage || portal_photos_storage`, so every URL here is a
   * self-hosted copy that does not expire. The old client-side
   * "prefer photos_storage[i], fall back to photos[i]" dance — and the
   * cross-member phash dedup that fed it — is gone: the projection does it once
   * per cycle for the whole catalogue.
   */
  photos: string[];
  /**
   * Origin of each item in `photos`, parallel-indexed by construction (the
   * projection builds it from the same concatenation). Carousel reads this to
   * render per-slide source tags.
   *
   * Deliberately `string[]`, not a union: the tags are DATA produced by the
   * pipeline. The union this used to be was already wrong — the DB holds
   * `adresowo` and `domiporta` too — and would need editing every time a portal
   * is added. Every consumer already declares `string[]` and renders the tag
   * verbatim; only `'owner'` is special-cased, and only to suppress the badge.
   */
  photoSources?: string[];
  /** Public FB Reel/Video URL — click target for fb-video carousel slides. */
  fb_video_url: string | null;
  /** Distinct sources across the cluster ('fb', 'olx', …) — portal coverage chips. */
  platforms?: string[] | null;
  posted_at: string | null;
  /**
   * ODKĄD MIESZKANIE JEST NA RYNKU (`flat.offers.market_since`) — najstarsza publikacja w
   * klastrze, liczona od ostatniego ponownego wystawienia (przerwa > 30 dni).
   *
   * NIE `first_seen_at`: tamta mówi, odkąd MY znamy wiersz, i 2 582 z 6 071 ofert ma tam
   * dzień zbudowania warstwy serwującej (pomiar z migracji `20260927132756`). Regułę
   * pokazywania (próg 14 dni, sufit korpusu) trzyma `src/lib/rynekOd.ts`.
   */
  market_since?: string | null;
  scraped_at: string;
  /** Informational expiry: set when depth-aware disappearance confirmed gone. */
  stale_at: string | null;
  /** Marketplace listings only: definitive sold flag. */
  marketplace_is_sold: boolean | null;
  post_kind: PostKind | null;
  posting_type: PostingType;
  extracted_rent_pln: number | null;
  /** Fixed monthly administration fee (czynsz administracyjny). */
  admin_fee_pln?: number | null;
  /** rent + known fees — the `costMode: 'total'` column. */
  total_monthly_cost_pln?: number | null;
  /** Nasz szacunek czynszu adm. (metraż × mediana zł/m² dzielnicy), gdy ogłoszenie go nie podaje. */
  admin_fee_estimate_pln?: number | null;
  /** `provided` z ogłoszenia, `estimated` nasz szacunek, `unknown` nie wiemy. */
  admin_fee_source?: 'provided' | 'estimated' | 'unknown' | null;
  /**
   * WYRÓŻNIKI (migracja 20260930114353, liczone przy projekcji — Konwencja 26):
   * „Po remoncie” WYKRYTE regułą na opisie (tylko oferty scrapowane bez właściciela).
   */
  po_remoncie?: boolean | null;
  /**
   * Czego dotyczy remont (migracja 20260930171524, claude-haiku-4-5 na zdaniu z opisu):
   * flat | kitchen | bathroom | building | common_areas | unclear; `null` = nie klasyfikowano.
   * Wyróżnik tylko za flat/kitchen/bathroom. Tylko strona oferty (useSourcePost).
   */
  remont_zakres?: string | null;
  /** Fraza czasu remontu przepisana dosłownie z opisu („w 2025 roku”, „świeżo”) albo `null`. */
  remont_kiedy?: string | null;
  /** Mediana kosztu z opłatami w komórce dzielnica × pokoje (≥ 20 ofert); `null` = brak porównania. */
  mediana_kosztu_okolicy_pln?: number | null;
  /** Liczność tej komórki — do zdania „mediana N ogłoszeń”. */
  ofert_w_porownaniu?: number | null;
  /** „Taniej niż podobne”: o ile % taniej od mediany (15–45); `null` = nie wyróżnik. */
  taniej_o_procent?: number | null;
  /**
   * OBNIŻKA CENY (migracja 20261007081536, krok po projekcji): czynsz przed obniżką i kiedy
   * pojawił się post z niższą ceną. Obie `null`, gdy obniżki nie ma albo nie jest wiarygodna
   * (spadek 3–30%, ostatnie 60 dni). Podwyżek baza nie zapisuje.
   */
  rent_reduced_from_pln?: number | null;
  rent_reduced_at?: string | null;
  extracted_rooms: number | null;
  extracted_area_m2: number | null;
  extracted_city: string | null;
  extracted_suburb: string | null;
  extracted_subdistrict: string | null;
  /** Geocoded district (authoritative where set), distinct from the author's `extracted_suburb`. */
  district?: string | null;
  extracted_lat: number | null;
  extracted_lon: number | null;
  extracted_extras: Record<string, unknown> | null;
  /* ── Pola portalowe: OPCJONALNE, bo zależą od tego, kto pyta ──────────────────────────
     DEC-04 odebrał anonowi grant na te osiem kolumn, więc zapytanie bez sesji ich NIE
     WYBIERA (patrz KOLUMNY_PORTALOWE w useSourcePost) i w obiekcie ich po prostu nie ma.
     `| null` kłamałoby: sugerowałoby „jest, tylko puste", a różnica jest istotna —
     `undefined` znaczy „nie wiesz, bo nie wolno Ci było zapytać". Konsumenci i tak chowają
     te elementy przed anonem (DEC-04), więc nie wymaga to zmian w komponentach. */
  portal_link_url?: string | null;
  portal_link_source?: string | null;
  portal_enriched_at?: string | null;
  portal_enrich_provider?: string | null;
  /**
   * The portal listing's description, narrowed in SQL from the enrichment blob.
   * Still raw HTML (that is how the portal serves it), so the client keeps
   * stripping tags — but the rest of `portal_enrichment_raw`, which included the
   * seller's phone and name, never reaches the browser now.
   */
  portal_description?: string | null;
  /** Source-side timestamp from the portal listing (e.g. OLX "Dodane" date). */
  portal_listing_updated_at?: string | null;
  /** What that timestamp represents on the portal: posted | refreshed | modified. */
  portal_listing_updated_kind?: string | null;
  /**
   * All portal links for this offer, unioned across cluster members by the
   * PROJECTION (it used to be `aggregatePortalLinks` running per page view).
   * Two origins, deduped by (source, url):
   *   - direct: a member's raw_text contained the URL
   *   - matched: find-portal-matches paired the member with a portal listing via
   *     phone or structural search-by-params (rows in `flat.portal_matches`).
   * `enriched` is true when we successfully scraped the portal description
   * (only direct links are enriched today).
   */
  portalLinks?: Array<{
    source: string;
    url: string;
    enriched: boolean;
    viaMatch?: boolean;
    confirmedVia?: 'phone' | 'structural';
    matchScore?: number;
    /** flat.portal_matches.id — populated only on viaMatch entries; used by
     * the Sources-tab "mark as false positive" debug control. */
    matchId?: string;
    /** True when a human has reviewed and confirmed this match. */
    matchConfirmed?: boolean;
  }>;
  /** How many members the cluster has, over its WHOLE membership. */
  cluster_size?: number | null;
  /** Number of cross-posts the Sources tab can actually list — the public
   * (scraped + active) slice of `flat.offer_post_map`, minus the head. */
  duplicateCount?: number;
  /**
   * The cross-posts themselves, from `flat.offer_post_map`. Ordered
   * newest-first.
   *
   * No `id`: the map's public grant withholds `source_post_id` by design, so a
   * cross-post is describable ("also in Grupa X on 12 July at 3200 zł") without
   * handing the browser a key into the raw layer. React keys come off
   * `source_url`, which is the author's own public post.
   */
  duplicates?: Array<{
    source_url: string;
    channel_name: string | null;
    channel_url: string | null;
    posted_at: string | null;
    extracted_rent_pln: number | null;
  }>;
  /** Min rent across the whole cluster. */
  clusterPriceMin?: number;
  /** Max rent across the cluster. When clusterPriceMin !== clusterPriceMax → price changed. */
  clusterPriceMax?: number;
  /** POI scores 0-100, computed from `flat.poi_features` summary. NULL when the
   * offer has no precise coords or Overpass enrichment hasn't completed yet. */
  walkability_score?: number | null;
  transit_score?: number | null;
  green_score?: number | null;
  /** Triggered badges (e.g. ['near_metro','green_neighbourhood']). Sorted
   * by raw threshold order; client picks top-N via BADGE_PRIORITY. */
  poi_badges?: string[] | null;
  metro_nearest_name?: string | null;
  metro_nearest_m?: number | null;
  /** Geocode tier ('nominatim', 'portal:coords', 'landmark:*', …) — drives pin
   * precision (geocodePrecision.ts) and gates the street label. */
  geocode_provider?: string | null;
  /**
   * The road name our geocoder resolved for this offer's point — tier 5 of the
   * street cascade in offerLocation.ts, and the tier that carries most of the
   * catalogue. Fetched only on the offer page (single row), never on the SERP.
   *
   * The ROAD only. This used to be `geocode_raw`, the whole Nominatim payload,
   * from which the front read `address.road` and pointedly ignored the house
   * number sitting next to it — a number that came from snapping to a building,
   * not from the author. `flat.offers` narrows it in SQL instead, so the
   * privacy rule is now structural rather than a matter of client-side
   * restraint. The admin console still reads the full `geocode_raw` off the raw
   * layer, where it belongs.
   */
  geocode_street?: string | null;
  /**
   * Verified owner behind this row: a verified claim or a verified manual
   * listing. Drives the "Zweryfikowany właściciel" badge; unverified owner rows
   * instead show the "owner is completing verification" note at the contact
   * surface.
   */
  owner_confirmed?: boolean | null;
  /**
   * Owner is present on FlatHero for this offer — a manual listing OR a claimed
   * offer — even before verification. Drives the middle trust tier ("Właściciel
   * na FlatHero") and premiering. `owner_confirmed` implies `owner_present`.
   */
  owner_present?: boolean | null;
}

/**
 * A row of the RAW layer, `flat.source_posts` — the admin console's shape.
 *
 * Everything here is a column the serving layer deliberately does not carry:
 * pipeline bookkeeping (parse status, hashes, canonical pointers), the
 * pre-durability photo columns the projection collapses into `photos`, the
 * enrichment blob it narrows into `portal_description`, and the scraped
 * author's personal data — name, profile URL, phone — which on the front is
 * reachable only through `flat.offer_contact(offer_id)`, one offer per call.
 *
 * Declared as an EXTENSION rather than a sibling because the raw row really
 * does carry both sets, and because it keeps the shared analysis helpers
 * (portalCoverageLinks, topPoiBadges) usable from the admin console without a
 * second overload. The direction of the relationship is the honest one: the
 * admin console can see everything the front can, and more.
 *
 * The one thing to hold onto: a `RawSourcePost.id` is a `source_posts.id`, and
 * a `SourcePost.id` is a `flat.offers.id`. They are not interchangeable, and
 * nothing on the front should ever receive one where it expects the other.
 */
export interface RawSourcePost extends SourcePost {
  /**
   * Self-hosted copies of the scraped `photos`, parallel-indexed. Each entry is
   * a public Supabase Storage URL or null where that index hasn't been
   * downloaded yet. On the front this distinction is already resolved —
   * `SourcePost.photos` is durable-only ([P11]).
   */
  photos_storage: (string | null)[] | null;
  /** Twin of `photos_storage` for portal (OLX/Otodom) CDN photos. */
  portal_photos_storage?: (string | null)[] | null;
  /** Parallel-indexed perceptual hashes (16-char hex dHash) for `photos`. */
  photo_phashes: string[] | null;
  parse_status: string;
  parse_confidence: number | null;
  raw_text_hash: string | null;
  /** DB-side cluster head ID, set by `flat.set_canonical_post_id`. */
  canonical_post_id: string | null;
  is_canonical: boolean | null;
  /** The full enrichment payload. The front gets only `portal_description`. */
  portal_enrichment_raw: Record<string, unknown> | null;
  /** Precomputed nearby POIs. The front calls `flat.nearby_map_pois` instead. */
  map_pois?: MapPoi[] | null;
  /** The scraped author's display name — personal data of a non-user. */
  raw_author_name: string | null;
  raw_author_url: string | null;
  extracted_phone_e164: string | null;
  /** The full geocoder payload, house number included. */
  geocode_raw?: Record<string, unknown> | null;
}

/** One precomputed map marker (flat.nearby_map_pois → source_posts.map_pois). */
export interface MapPoi {
  category: string;
  name: string;
  lat: number;
  lon: number;
  distance_m: number;
  /** OSM subtype (amenity/leisure/…) — drives within-category display priority
   * (pharmacy/clinic/hospital before dentist; cinema/theatre before galleries). */
  subtype?: string | null;
}

/** SERP card shows max 3 badges. Order = "decision-impact" priority for
 * Warsaw renters in 2026: metro/tram first (commute), then groceries +
 * restaurants (lifestyle), then niche (school/pharmacy/culture). Anti-features
 * (`quiet_corner`) live only in the OfferPage detail section, not SERP. */
export const BADGE_PRIORITY = [
  'near_metro',
  'near_rail',
  'tram_at_door',
  'green_neighbourhood',
  'grocery_hub',
  'restaurant_hub',
  'school_walk',
  'pharmacy_corner',
  'culture_rich',
  // 'quiet_corner' deliberately excluded — show only in detail page.
] as const;
export type PoiBadge = typeof BADGE_PRIORITY[number] | 'quiet_corner';

export function topPoiBadges(badges: string[] | null | undefined, n: number = 3): PoiBadge[] {
  if (!badges || badges.length === 0) return [];
  // `green_neighbourhood` WRÓCIŁO 30.09 wieczorem — wynik liczy się z `flat.park_areas`
  // (wielokąty, krawędź, bez ogródków). `quiet_corner` i tak nie jest w BADGE_PRIORITY, więc na
  // karcie SERP nie było go nigdy; wstrzymanie na ofercie siedzi w NawigacjaOkolicy (WSTRZYMANE).
  const ranked = BADGE_PRIORITY.filter((b) => badges.includes(b));
  return ranked.slice(0, n);
}

/**
 * Is there an owner behind this offer? The one place the front asks that.
 *
 * Every surface that used to ask `origin === 'owner'` now has THREE values to
 * reason about, and two of them mean the same thing here: a claimed offer and a
 * manual owner offer are both entities an owner controls, so both hide the
 * scraped-author contact, the Facebook button and the "this is my flat" claim
 * entry, and both expose messaging. Only `'scraped'` is the other case.
 *
 * Written as a negation of `'scraped'` rather than a list of owner values on
 * purpose: a fourth owner-side origin (a partner feed, an imported portfolio)
 * is then handled the day the projection starts emitting it, instead of
 * silently falling through to the scraped branch — which is the failure mode
 * that would be least visible and most wrong.
 *
 * The `?? 'scraped'` default matters more than it looks. A `SourcePost` can
 * arrive as slim SERP route state or a hand-built test fixture with no origin,
 * and the honest reading of "we do not know" is the conservative one: treat it
 * as scraped, because the scraped branch shows the public post's own contact
 * surfaces, while the owner branch would offer to message an owner who may not
 * exist.
 */
export function isOwnerOffer(post: Pick<SourcePost, 'origin'> | null | undefined): boolean {
  return (post?.origin ?? 'scraped') !== 'scraped';
}


/**
 * Row from `flat.portal_matches` — produced by find-portal-matches EF when
 * search-by-params confirms an FB post matches an OLX/Otodom listing.
 */
export interface PortalMatchRow {
  id: string;
  source_post_id: string;
  portal_source: string;
  portal_url: string;
  match_score: number | null;
  confirmed_via: string | null;
  /** Human review: true once an authenticated user has confirmed the match.
   * Decorative; doesn't change visibility. */
  confirmed?: boolean;
}

/**
 * Pull rows from `flat.portal_matches` for a set of post IDs, chunked to
 * avoid the supabase-js `.in()` URL truncation past ~50 UUIDs (see memory:
 * `feedback_postgrest_in_url_cap`). Returns a Map keyed by source_post_id.
 */
export async function fetchPortalMatches(postIds: string[]): Promise<Map<string, PortalMatchRow[]>> {
  const out = new Map<string, PortalMatchRow[]>();
  if (postIds.length === 0) return out;
  const CHUNK = 40;
  const slices: string[][] = [];
  for (let i = 0; i < postIds.length; i += CHUNK) slices.push(postIds.slice(i, i + CHUNK));
  // Parallel, not sequential — N chunk round-trips were serialising the SERP load.
  const results = await Promise.all(
    slices.map((slice) =>
      supabase
        .schema('flat' as never)
        .from('portal_matches' as never)
        .select('id,source_post_id,portal_source,portal_url,match_score,confirmed_via,confirmed')
        .eq('false_positive', false)
        .in('source_post_id', slice),
    ),
  );
  for (const { data, error } of results) {
    if (error) throw error;
    for (const row of (data ?? []) as unknown as PortalMatchRow[]) {
      const arr = out.get(row.source_post_id) ?? [];
      arr.push(row);
      out.set(row.source_post_id, arr);
    }
  }
  return out;
}


/**
 * Platform-coverage set. Every cluster head is implicitly on `fb` (the
 * source). Adding a portal (`olx`/`otodom`/`morizon`/`gratka`/…) indicates we
 * found a match there (direct link or via find-portal-matches). The SERP
 * filter uses CONTAINS semantics: a selected set returns flats present on AT
 * LEAST those portals (possibly more). Selecting only {fb} is a no-op (every
 * flat is on fb).
 */
export type Platform = 'fb' | 'olx' | 'otodom' | 'morizon' | 'gratka' | 'nieruchomosci-online';


/**
 * Slim SERP row: only what the card, the map popup and the OfferPage
 * instant-paint actually render. `Pick` keeps it mechanically in sync with
 * `SourcePost`; the optional decorated field (photoSources)
 * is simply absent on freshly fetched SERP rows.
 *
 * No `photos_storage`: the serving layer's `photos` is durable-only already
 * (see SERP_COLUMNS).
 */
export type SerpPost = Pick<
  SourcePost,
  | 'id' | 'source' | 'source_url' | 'posted_at' | 'stale_at' | 'marketplace_is_sold'
  | 'posting_type' | 'extracted_rent_pln' | 'extracted_rooms' | 'extracted_area_m2'
  | 'extracted_city' | 'extracted_suburb' | 'extracted_subdistrict' | 'extracted_lat' | 'extracted_lon'
  | 'extracted_extras' | 'photos' | 'fb_video_url' | 'geocode_provider' | 'market_since'
  | 'walkability_score' | 'transit_score' | 'green_score' | 'poi_badges'
  | 'metro_nearest_name' | 'metro_nearest_m'
  | 'photoSources' | 'origin' | 'owner_confirmed' | 'owner_present'
  | 'po_remoncie' | 'mediana_kosztu_okolicy_pln' | 'ofert_w_porownaniu' | 'taniej_o_procent'
  | 'rent_reduced_from_pln' | 'rent_reduced_at'
> & {
  platforms?: string[];
  total_monthly_cost_pln?: number | null;
  /**
   * Tylko na ścieżce rankingu (`flat.offers_ranked`, aktywny filtr miękki): klucz dopasowania
   * (sortowanie) i kolumny, których adnotacja karty nie może wziąć z `extras`.
   */
  dopasowanie_klucz?: number | null;
  has_elevator?: boolean | null;
  deposit_pln?: number | null;
  amenities_norm?: string[] | null;
  /**
   * Skąd wzięła się opłata administracyjna: `provided` z ogłoszenia, `estimated` nasz
   * szacunek z metrażu, `unknown` nie wiemy. Decyduje o tym, czy przy kwocie stoi „ok."
   * i czy oferta w ogóle wchodzi do filtra kosztu (SRCH-15).
   */
  admin_fee_source?: 'provided' | 'estimated' | 'unknown' | null;
};

/** Live result count for the filter sheet's apply button ("Pokaż N ofert").
 * HEAD request — count only, no rows. */
/** Ten plik czyta zawsze przez klienta aplikacji; moduł ścieżki odczytu dostaje go jako argument. */
function serpSelect(filters: SourcePostFilters, cols: string, count?: { count: 'exact'; head?: boolean }) {
  return serpSelectModulu(supabase as unknown as SerpClient, filters, cols, count);
}


export function useSerpCount(filters: SourcePostFilters = {}, opts?: { enabled?: boolean }) {
  return useQuery<number | null>({
    queryKey: ['flat.serp_count', serpFilterKey(filters)],
    enabled: opts?.enabled ?? true,
    staleTime: 60_000,
    queryFn: async () => {
      let q = serpSelect(filters, 'id', { count: 'exact', head: true });
      q = applySerpFilters(q as never, filters) as never;
      const { count, error } = await q;
      if (error) throw error;
      return count;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * Ile ofert w SEKCJI listy przy filtrach miękkich (Piotr 29.09): „Pozostałe N ofert” pod kreską
 * (`podKreska`) i „N z M ogłoszeń potwierdza wybrane cechy” w stopce panelu (`potwierdzone`).
 * Ten sam łańcuch co lista i licznik (ścieżka rankingu) + warunek sekcji z `applySekcja`, więc
 * nad kreską + pod kreską + bez zdjęć = licznik. Bez filtrów miękkich — nie pyta (kreski nie ma).
 */
export function useLiczbaSekcji(filters: SourcePostFilters, sekcja: SekcjaListy, opts?: { enabled?: boolean }) {
  return useQuery<number | null>({
    queryKey: ['flat.serp_sekcja', sekcja, serpFilterKey(filters)],
    enabled: (opts?.enabled ?? true) && maMiekkieFiltry(filters),
    staleTime: 60_000,
    queryFn: async () => {
      let q = serpSelect(filters, 'id', { count: 'exact', head: true });
      q = applySerpFilters(q as never, filters) as never;
      q = applySekcja(q as never, sekcja) as never;
      const { count, error } = await q;
      if (error) throw error;
      return count;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * Liczniki dla KILKU zestawów filtrów naraz — ten sam łańcuch i ten sam klucz pamięci co
 * `useSerpCount`, więc zestaw już policzony gdzie indziej nie idzie drugi raz. Służy pustemu
 * wynikowi z „Mojego wyszukiwania": które filtry najbardziej zawężają.
 */
export function useLiczbySerp(zestawy: SourcePostFilters[], opts?: { enabled?: boolean }) {
  return useQueries({
    queries: zestawy.map((filters) => ({
      queryKey: ['flat.serp_count', serpFilterKey(filters)],
      enabled: opts?.enabled ?? true,
      staleTime: 60_000,
      queryFn: async () => {
        let q = serpSelect(filters, 'id', { count: 'exact', head: true });
        q = applySerpFilters(q as never, filters) as never;
        const { count, error } = await q;
        if (error) throw error;
        return count as number | null;
      },
    })),
  });
}

/**
 * ILE OFERT ZABRAŁA REGUŁA „BEZ INFORMACJI O OPŁATACH" (SRCH-15).
 *
 * Przy liczniku w trybie kosztu mówimy: „N ofert bez informacji o opłatach pominięto".
 * Bez tego zdania filtr po cichu zjada 157 ofert i wygląda to jak brak podaży, a nie jak
 * świadoma reguła. Liczy się to TYM SAMYM łańcuchem filtrów, tylko z regułą wyłączoną i z
 * warunkiem odwrotnym — czyli dokładnie tym, co reguła zabrała, a nie osobnym przybliżeniem.
 */
export function useLiczbaBezOplat(filters: SourcePostFilters = {}, opts?: { enabled?: boolean }) {
  return useQuery<number | null>({
    queryKey: ['flat.serp_count', 'bez-oplat', serpFilterKey(filters)],
    enabled: opts?.enabled ?? true,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      let q = serpSelect(filters, 'id', { count: 'exact', head: true });
      q = applySerpFilters(q as never, filters, { pomijajRegulaOplat: true }) as never;
      const { count, error } = await (q as unknown as {
        eq(col: string, v: unknown): Promise<{ count: number | null; error: unknown }>;
      }).eq('admin_fee_source', 'unknown');
      if (error) return null;
      return count;
    },
    placeholderData: keepPreviousData,
  });
}

/** Minimal row for map pins — the popup detail comes from useSerpHead on tap. */
export interface SerpPin {
  id: string;
  extracted_lat: number | null;
  extracted_lon: number | null;
  geocode_provider: string | null;
  extracted_suburb: string | null;
  extracted_rent_pln: number | null;
  /** 1 = zdjęcia i cena, 2 = brakuje jednego, 3 = brakuje obu (krok O). */
  klasa_jakosci?: number | null;
  /** Koszt najmu (czynsz + opłaty) — pigułka pokazuje go w trybie kosztu. */
  total_monthly_cost_pln?: number | null;
  admin_fee_source?: 'provided' | 'estimated' | 'unknown' | null;
  /** Powód niepewnej ceny albo `null`; niepusty = pin bez etykiety z kwotą. */
  niepewny_powod?: string | null;  /** Klucz dopasowania filtrów miękkich (ścieżka rankingu); 0 = potwierdza wszystkie. */
  dopasowanie_klucz?: number | null;
}

interface SerpPage {
  rows: SerpPost[];
  count: number | null;
}

// NOTE: the old withDurablePhotos() rebuild is gone. `flat.offers.photos` is
// already `photos_storage || portal_photos_storage` — durable bucket URLs only,
// by construction ([P11]) — so there is no expiring FB CDN URL left to prefer
// away from, and no photos_storage column to rebuild against.

/**
 * Jedna strona listy. Eksport tylko dla testu zgodności wczesnej listy (`wczesnaLista.test.ts`):
 * to ten łańcuch wyznacza, jak ma wyglądać zapytanie skryptu w `index.html`.
 */
export async function fetchSerpPage(filters: SourcePostFilters, page: number): Promise<SerpPage> {
  // Pierwsza strona gołego /search mogła już przyjść z `index.html` (src/lib/wczesnaLista.ts).
  const wczesna = zabierzWczesnaListe(filters, page);
  if (wczesna) {
    try {
      return await wczesna;
    } catch {
      // Wczesne zapytanie padło: pytamy zwykłą drogą, jakby go nie było.
    }
  }
  // Ten sam `buildQuery`, którym `send-filter-alerts` wybierze oferty do e-maila (plan §1.68).
  const q = buildQuery(supabase as unknown as SerpClient, filters, filters.sort, { count: { count: 'exact' } });
  const from = page * SERP_PAGE_SIZE;
  const { data, error, count } = await q.range(from, from + SERP_PAGE_SIZE - 1);
  if (error) throw error;
  return { rows: (data ?? []) as unknown as SerpPost[], count };
}

/**
 * Paginated SERP read from the serving layer: ONE row per offer, platforms[]
 * pre-aggregated, filters/sort/count all DB-side. Pages of SERP_PAGE_SIZE via
 * `.range()` — the old single-shot fetch shipped the whole view (5.6 MB and
 * growing) before first paint.
 *
 * NOTE: no client portalLinks decoration here (it ran N chunked queries per
 * load — the old main bottleneck); the coverage chip reads the view's
 * `platforms` array via portalCoverageLinks. OfferPage still fully decorates.
 */
export function useSourcePostsInfinite(filters: SourcePostFilters = {}, opts?: { enabled?: boolean }) {
  return useInfiniteQuery({
    // serpFilterKey drops `view`, so a list↔map toggle never busts this cache.
    queryKey: ['flat.offers', 'pages', serpFilterKey(filters)],
    queryFn: ({ pageParam }) => fetchSerpPage(filters, pageParam),
    initialPageParam: 0,
    getNextPageParam: getNextSerpPageParam,
    staleTime: 60_000,
    // Keep the previous results on screen while a filter change refetches, so the
    // list/count/header don't collapse to a skeleton (which shifted everything
    // below). isLoading stays false after the first load; isFetching covers the
    // in-flight refresh.
    placeholderData: keepPreviousData,
    enabled: opts?.enabled ?? true,
  });
}

/**
 * Map mode: every filtered head as a lightweight pin (7 kolumn zamiast pełnego wiersza).
 * Szczegóły oferty dociąga `useSerpHead` po dotknięciu pinu.
 *
 * KILKA ZAPYTAŃ PO TYSIĄCU ZAMIAST JEDNEGO OBCIĘTEGO (decyzja Piotra 27.09). PostgREST ma
 * twardy sufit `db-max-rows = 1000`, więc jedno zapytanie pokazywało tysiąc pinów z czterech
 * tysięcy i nie dało się tego obejść inaczej niż podnosząc sufit dla CAŁEJ bazy — czyli
 * także dla zapytań, które mają być małe. Dociągamy więc strony aż do wyczerpania `count`.
 *
 * TRZY RZECZY, BEZ KTÓRYCH TO NIE DZIAŁA, i każda z nich jest tu z powodu:
 *
 * 1. KOLEJNOŚĆ TOTALNA: klasa jakości → data → `id`. Bez `id` dwie oferty z tą samą datą
 *    mogą przy kolejnych zapytaniach wypaść po różnych stronach granicy strony — jedna
 *    zniknie, druga pojawi się dwa razy. To nie jest teoria: `applySerpSort` dokleja `id` z
 *    dokładnie tego powodu, a zapytanie o piny dotąd stronicowania nie miało.
 * 2. PIERWSZA STRONA RYSUJE SIĘ OD RAZU, RESZTA RÓWNOLEGLE (29.09). Pierwsza strona podaje
 *    `count`, więc strony 2…n idą naraz (`stronyPinowDoPobrania`), a nie jedna po drugiej.
 *    Etykiety z bliska pojawiają się jak dotąd z pierwszą stroną; widok miasta czeka na
 *    `kompletne`, bo liczby kół z części stron były zaniżone („Mokotów 129” → „558”).
 * 3. ZMIANA FILTRÓW PRZERYWA DOCIĄGANIE. Klucz każdej strony zawiera filtry, więc stare strony
 *    nie trafiają do nowego wyniku, a `signal` z `queryFn` idzie do PostgREST-a (`abortSignal`)
 *    i porzucone zapytania są przerywane.
 */
export const PINY_NA_STRONE = PINS_LIMIT;
/**
 * Sufit bezpieczeństwa. Przy dzisiejszych ~4 tysiącach ofert pełny zestaw to cztery–pięć
 * stron; dziesięć daje zapas na wzrost i jednocześnie pilnuje, żeby błąd w warunku
 * zakończenia nie zamienił się w nieskończoną pętlę zapytań do bazy.
 */
export const MAKS_STRON_PINOW = 10;

export interface StronaPinow {
  pins: SerpPin[];
  count: number | null;
}

async function pobierzStronePinow(
  filters: SourcePostFilters,
  strona: number,
  signal?: AbortSignal,
): Promise<StronaPinow> {
  let q = serpSelect(filters, PIN_COLUMNS, { count: 'exact' });
  q = applySerpFilters(q as never, filters) as never;
  const od = strona * PINY_NA_STRONE;
  /**
   * WSZYSTKIE oferty, także bez punktu i bez dzielnicy (decyzja Piotra 30.09). Do 30.09 stał tu
   * warunek „ma współrzędne ALBO dzielnicę”, więc ~200 ofert bez jednego i drugiego nie przychodziło
   * wcale i suma kół w widoku miasta nie zgadzała się z licznikiem (4 467 wobec 4 679). Teraz
   * `pinFor` stawia je w środku miasta jako „lokalizacja nieznana”.
   */
  const qq = q;
  // Kolejność tablicy pinów jest na mapie PRIORYTETEM: z niej bierze się `symbol-sort-key`,
  // czyli to, czyja pigułka z ceną przetrwa kolizję, kto reprezentuje stos i w jakiej
  // kolejności stoją karty w karuzeli (krok O). `id` na końcu trzyma strony rozłączne.
  const posortowane = applyPinSort(qq as never, sciezkaOdczytu(filters)) as unknown as {
    range: (od: number, do_: number) => {
      abortSignal: (s: AbortSignal) => Promise<{ data: unknown; error: unknown; count: number | null }>;
    };
  };
  const { data, error, count } = await posortowane
    .range(od, od + PINY_NA_STRONE - 1)
    .abortSignal(signal as AbortSignal);
  if (error) throw error;
  return { pins: (data ?? []) as unknown as SerpPin[], count };
}

/**
 * Które strony pinów pobrać poza zerową. RÓWNOLEGLE (29.09): po pierwszej stronie znamy `count`,
 * więc wszystkie następne idą naraz, a nie jedna po drugiej — zmierzone na telefonie: pięć stron
 * po ~180 ms dawało pełne liczby kół po 1,47 s, na 3G po 7,5 s (każda strona ~0,85 s).
 * Bez licznika (`count` = null) dociągamy po kolei jak dotąd: następna strona tylko po pełnej
 * poprzedniej. Krótka pierwsza strona = koniec zbioru. Sufit `MAKS_STRON_PINOW` zawsze.
 */
export function stronyPinowDoPobrania(strony: ReadonlyArray<StronaPinow | undefined>): number[] {
  const p0 = strony[0];
  if (!p0 || p0.pins.length < PINY_NA_STRONE) return [];
  if (p0.count != null) {
    const n = Math.min(Math.ceil(p0.count / PINY_NA_STRONE), MAKS_STRON_PINOW);
    return Array.from({ length: Math.max(0, n - 1) }, (_, k) => k + 1);
  }
  const wynik: number[] = [];
  for (let i = 1; i < MAKS_STRON_PINOW; i++) {
    wynik.push(i);
    const s = strony[i];
    if (!s || s.pins.length < PINY_NA_STRONE) break;
  }
  return wynik;
}

/**
 * Piny ze stron w KOLEJNOŚCI STRON (kolejność = priorytet na mapie). Dopóki strony dochodzą —
 * tylko ciągły prefiks od zerowej: strona 3 bez 2 przestawiłaby priorytety pigułek i za chwilę
 * znowu. Gdy wszystko rozstrzygnięte — każda strona, która przyszła (błąd jednej nie zabiera
 * reszty). Bez duplikatów po `id`: strony liczone w innych chwilach mogą się zazębić, gdy
 * projekcja zmieni zbiór między zapytaniami.
 */
export function scalStronyPinow(strony: ReadonlyArray<StronaPinow | undefined>, rozstrzygniete: boolean): SerpPin[] {
  const wynik: SerpPin[] = [];
  const byly = new Set<string>();
  for (const s of strony) {
    if (!s) {
      if (!rozstrzygniete) break;
      continue;
    }
    for (const p of s.pins) {
      if (byly.has(p.id)) continue;
      byly.add(p.id);
      wynik.push(p);
    }
  }
  return wynik;
}

export interface PinySerp {
  pins: SerpPin[];
  count: number | null;
  /**
   * Czy wszystkie strony są rozstrzygnięte (przyszły albo padły po ponowieniu). Widok miasta
   * rysuje koła dopiero wtedy — inaczej pokazywał częściowe liczby („Mokotów 129” → „558”).
   */
  kompletne: boolean;
}

export function useSerpPins(filters: SourcePostFilters = {}, opts?: { enabled?: boolean }) {
  const wlaczone = opts?.enabled ?? true;
  const klucz = serpFilterKey(filters);
  const pierwsza = useQuery<StronaPinow>({
    queryKey: ['flat.serp_pins', klucz, 0],
    queryFn: ({ signal }) => pobierzStronePinow(filters, 0, signal),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    enabled: wlaczone,
  });
  // Dopóki pierwsza strona jest z POPRZEDNICH filtrów (placeholder), następnych nie pytamy —
  // liczba stron zależy od jej `count`.
  const p0 = pierwsza.isPlaceholderData ? undefined : pierwsza.data;

  // Strony liczone z tego, co już jest: przy znanym `count` lista jest pełna od razu, bez niego
  // rośnie o jedną po każdej pełnej stronie. Czytamy CACHE synchronicznie w renderze — przyjście
  // strony i tak wywołuje render (obserwator `useQueries`), a w nim lista jest już aktualna.
  // (Ref z poprzedniego renderu tu nie działa: po ostatniej stronie nic nie wywołałoby renderu,
  // który dopisałby następną.)
  const qc = useQueryClient();
  const zCache = Array.from({ length: MAKS_STRON_PINOW - 1 }, (_, k) =>
    qc.getQueryData<StronaPinow>(['flat.serp_pins', klucz, k + 1]));
  const indeksy = p0 ? stronyPinowDoPobrania([p0, ...zCache]) : [];
  const reszta = useQueries({
    queries: indeksy.map((i) => ({
      queryKey: ['flat.serp_pins', klucz, i],
      queryFn: ({ signal }: { signal: AbortSignal }) => pobierzStronePinow(filters, i, signal),
      staleTime: 60_000,
      // Jedno ponowienie, potem błąd = strona rozstrzygnięta. Domyślne trzy z narastającym
      // odstępem trzymałyby koła miasta w oczekiwaniu do ~7 s.
      retry: 1,
      enabled: wlaczone && !!p0,
    })),
  });

  const strony: Array<StronaPinow | undefined> = [p0];
  indeksy.forEach((i, k) => { strony[i] = reszta[k]?.data; });
  const rozstrzygniete = !!p0 && reszta.every((r) => r.isSuccess || r.isError)
    && stronyPinowDoPobrania(strony).length === indeksy.length;
  // Stała długość listy zależności (React tego wymaga, a liczba stron rośnie z 0 do n):
  // zmiany danych dalszych stron niesie jeden napis z ich `dataUpdatedAt`.
  const znacznikReszty = reszta.map((r) => `${r.dataUpdatedAt}:${r.status}`).join(',');
  const piny = useMemo(
    () => scalStronyPinow(strony, rozstrzygniete),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `strony` odtwarzane co render; zmiany niesie `znacznikReszty`
    [p0, rozstrzygniete, znacznikReszty],
  );

  // Przy zmianie filtrów pokazujemy POPRZEDNI komplet, dopóki nie przyjdzie nowy (jak dawny
  // `keepPreviousData` na zapytaniu nieskończonym) — nie samą jego pierwszą stronę.
  const ostatni = useRef<PinySerp | undefined>(undefined);
  let data: PinySerp | undefined;
  if (pierwsza.isPlaceholderData) data = ostatni.current;
  else if (p0) {
    data = { pins: piny, count: p0.count ?? null, kompletne: rozstrzygniete };
    if (rozstrzygniete) ostatni.current = data;
  }
  const pobieraReszte = reszta.some((r) => r.isFetching);
  return {
    data,
    isLoading: pierwsza.isLoading,
    error: pierwsza.error,
    isPlaceholderData: pierwsza.isPlaceholderData,
    isFetching: pierwsza.isFetching || pobieraReszte,
    /** Zachowana nazwa z czasów zapytania nieskończonego: pierwsza strona jest, dalsze idą. */
    isFetchingNextPage: !pierwsza.isFetching && pobieraReszte,
  };
}

/** One slim offer row — powers the map-popup detail, fetched when the popup opens. */
export function useSerpHead(id: string | undefined) {
  return useQuery<SerpPost | null>({
    queryKey: ['flat.offers', 'one', id],
    enabled: !!id,
    queryFn: async () => {
      if (!id) return null;
      // No liveness/grace filters here: the popup only opens for a pin the SERP
      // already handed out, and a direct offer link must resolve even for a row
      // the list-side filters would hide.
      const { data, error } = await supabase
        .schema('flat' as never)
        .from(SERP_TABLE as never)
        .select(SERP_COLUMNS)
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as SerpPost) ?? null;
    },
    staleTime: 60_000,
  });
}

/**
 * Kilka ofert naraz — karuzela kart nad mapą (etap 2).
 *
 * JEDNO zapytanie na cały stos, nie jedno na kartę. W punktach wspólnych stoi nawet
 * kilkanaście ofert (20% pinów to punkty wspólne — centroidy dzielnic), więc `useSerpHead` na
 * kartę znaczyłoby kilkanaście równoległych zapytań przy jednym dotknięciu kropki.
 *
 * KOLEJNOŚĆ WEJŚCIOWA JEST ZACHOWANA. PostgREST zwraca wiersze w kolejności, w jakiej chce
 * baza, a karty mają iść w tej samej kolejności co piny, czyli wedle sortowania listy — bo to
 * jedna i ta sama odpowiedź na pytanie „co jest najważniejsze". Układamy więc wynik po stronie
 * klienta, wedle podanych identyfikatorów.
 */
export function useSerpHeads(ids: string[]) {
  const klucz = ids.join(',');
  return useQuery<SerpPost[]>({
    queryKey: ['flat.offers', 'kilka', klucz],
    enabled: ids.length > 0,
    queryFn: async () => {
      if (ids.length === 0) return [];
      const { data, error } = await supabase
        .schema('flat' as never)
        .from(SERP_TABLE as never)
        .select(SERP_COLUMNS)
        .in('id', ids);
      if (error) throw error;
      const wedlugId = new Map((data as unknown as SerpPost[] ?? []).map((r) => [r.id, r]));
      return ids.map((id) => wedlugId.get(id)).filter((r): r is SerpPost => !!r);
    },
    staleTime: 60_000,
  });
}

/**
 * SERP portal-coverage chips straight from the serving layer's `platforms`
 * array (`['fb','olx',...]`). The SERP badge is a non-clickable "also on OLX/Otodom"
 * indicator, so it needs only the source — no per-row client decoration (that ran N
 * chunked queries per SERP load and was the main load-time cost). Clickable portal
 * URLs are still decorated on the OfferPage (useSourcePost).
 */
export function portalCoverageLinks(post: SerpPost | SourcePost): { source: string; enriched: boolean }[] {
  return ((post as { platforms?: string[] }).platforms ?? [])
    .filter((p) => p !== 'fb')
    .map((source) => ({ source, enriched: false }));
}

/**
 * Official Warsaw "dzielnice" — only these appear in the SERP filter dropdown.
 * Non-Warsaw towns (Ząbki, Jabłonna, Mysiadło, Dobra) and ambiguous values
 * stored in extracted_suburb are filtered out at the UI layer.
 */
const WARSAW_OFFICIAL_DISTRICTS = new Set([
  'Bemowo',
  'Białołęka',
  'Bielany',
  'Mokotów',
  'Ochota',
  'Praga-Południe',
  'Praga-Północ',
  'Rembertów',
  'Śródmieście',
  'Targówek',
  'Ursus',
  'Ursynów',
  'Wawer',
  'Wesoła',
  'Wilanów',
  'Włochy',
  'Wola',
  'Żoliborz',
]);

export function useSourcePostSuburbs() {
  return useQuery<string[]>({
    queryKey: ['flat.offers.suburbs'],
    queryFn: async () => {
      // Counts the districts the SERP can actually return, so the dropdown never
      // offers a district with zero results. The old `parse_status='ok'` gate is
      // implicit: only successfully parsed posts get projected into flat.offers.
      const { data, error } = await supabase
        .schema('flat' as never)
        .from(SERP_TABLE as never)
        .select('extracted_suburb:suburb')
        .eq('status', 'active')
        .eq('post_kind', 'offer_whole_apartment')
        .not('suburb', 'is', null);
      if (error) throw error;
      const counts = new Map<string, number>();
      (data ?? []).forEach((row: { extracted_suburb: string | null }) => {
        if (row.extracted_suburb && WARSAW_OFFICIAL_DISTRICTS.has(row.extracted_suburb)) {
          counts.set(row.extracted_suburb, (counts.get(row.extracted_suburb) ?? 0) + 1);
        }
      });
      return Array.from(counts.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([name]) => name);
    },
    staleTime: 5 * 60_000,
  });
}
